import { CoursesFilterContext } from "@cs/screens/courses-beta/context"
import { chapterDifficulty } from "interfaces/courses"
import { Trans, useTranslation } from "next-i18next"
import { FC, ReactElement, useContext, useEffect, useState } from "react"
import { BiTime } from "react-icons/bi"
import { BsFillPeopleFill } from "react-icons/bs"
import { LuGraduationCap, LuRefreshCcw } from "react-icons/lu"
import styles from "./InfoPills.module.scss"
import { localeToDashCase } from "@cs/lib/modLocaleToDashCase"
import { useRouter } from "next/router"

interface InfoPillsProps extends React.HTMLAttributes<HTMLDivElement> {
  timeToFinish: number
  students?: number
  difficulty?: chapterDifficulty
  calculateTimeWithPace?: boolean
  lastUpdated?: number
}

interface InfoPillProps {
  Icon: FC
  content: string | Element
  element?: ReactElement
}

const InfoPill: FC<InfoPillProps> = ({ Icon, content, element }) => (
  <span className={styles.pill}>
    <Icon />
    {content}
    {element}
  </span>
)

const InfoPills: FC<InfoPillsProps> = ({
  timeToFinish,
  students,
  difficulty,
  lastUpdated,
  calculateTimeWithPace,
  className,
}) => {
  const { t } = useTranslation("courses")
  const { pace } = useContext(CoursesFilterContext)
  const [timeLeftState, setTimeLeftState] = useState<{ timeLeft: string; time: string }>({
    timeLeft: "",
    time: "",
  })
  const router = useRouter()

  useEffect(() => {
    setTimeLeftState(getTimeLeft(timeToFinish))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeToFinish, pace])

  const getTimeLeft = (timeLeftInMilliseconds: number) => {
    const timeLeftInDays = timeLeftInMilliseconds / (600000 * pace)
    let timeLeftString = ""
    let timeUnit = ""

    if (!calculateTimeWithPace || timeLeftInDays < 1) {
      return {
        timeLeft: String(timeLeftInMilliseconds / 60000).substring(0, 4),
        time: "Minutes",
      }
    }

    if (timeLeftInDays < 1 / 48) {
      // Less than half an hour
      timeLeftString = "Less than half an hour"
      timeUnit = ""
    } else if (timeLeftInDays < 1 / 24) {
      // Less than an hour
      timeLeftString = "Less than an hour"
      timeUnit = ""
    } else if (timeLeftInDays < 1) {
      // Less than a day
      timeLeftString = "A few hours"
      timeUnit = ""
    } else if (timeLeftInDays < 2) {
      // Less than two days
      timeLeftString = "One day"
      timeUnit = ""
    } else if (timeLeftInDays < 7) {
      // Less than a week
      timeLeftString = "A few days"
      timeUnit = ""
    } else if (timeLeftInDays < 14) {
      // Less than two weeks
      timeLeftString = "One week"
      timeUnit = ""
    } else if (timeLeftInDays < 30) {
      // Less than a month
      timeLeftString = "A few weeks"
      timeUnit = ""
    } else {
      timeLeftString = `${Math.round(timeLeftInDays / 30)}`
      timeUnit = "Month(s)"
    }

    return { timeLeft: timeLeftString, time: timeUnit }
  }

  const getNumberOfStudents = (students: number) => {
    if (students < 1000) {
      return students
    }

    return `${Math.floor(students / 1000)}k`
  }

  if (timeToFinish == 0 || timeLeftState.timeLeft == "Inf") {
    return <></>
  }

  return (
    <div
      className={`
      ${styles.information}
      ${className}
    `}
    >
      {timeToFinish != 0 && timeLeftState.timeLeft != "Inf" && (
        <>
          <InfoPill Icon={BiTime} content={`${timeLeftState.timeLeft} ${t(timeLeftState.time)}`} />
        </>
      )}
      {students && timeToFinish != 0 && <div className={styles.vr} />}
      {students && (
        <>
          <InfoPill
            Icon={BsFillPeopleFill}
            content={t("enrolled-students", { students: getNumberOfStudents(students) })}
          />
        </>
      )}
      {students && difficulty && <div className={styles.vr} />}
      {difficulty && (
        <>
          <InfoPill Icon={LuGraduationCap} content={t(difficulty)} />
        </>
      )}
      {lastUpdated &&  (
        <>
          <InfoPill Icon={LuRefreshCcw} content="" element={
            <Trans
              t={t}
              i18nKey="updated-on"
              values={{
                date: new Date(lastUpdated).toLocaleDateString(
                  localeToDashCase(router.locale),
                  { weekday: "long", year: "numeric", month: "long", day: "numeric" },
                ),
              }}
            />}
          />
        </>
      )}
    </div>
  )
}

export default InfoPills
